<template>
  <div class="buy-credit-wrapper" v-if="!userLocationLoading">
    <!-- <h1 class="header">{{ $t(isRedeemVoucher ? 'processEvent.voucher.header' : 'processEvent.buy.header') }}</h1> -->
    <buy-credit-or-redeem-voucher-form
      v-if="!isPurchasePackView && !phoneSearch"
      :country="country"
      :isPhonePrefilled="isPhonePrefilled"
      :fullPhoneNumber="fullPhoneNumber"
      :phoneNumber="phonenumber"
      :invalidPhonenumber="invalidPhonenumber"
      :packs="packs"
      :isLoading="isLoading"
      @update:selectedCountry="selectCountry"
      @update:phoneNumber="changePhoneNumber"
      @update:purchasePack="updatePurchasePacks"
      @submit="submit"
    />

    <buy-credit
      v-if="isPurchasePackView && packs"
      :packs="packs"
      :country="country"
      :phoneNumber="fullPhoneNumber"
      :isPhonePrefilled="isPhonePrefilled"
      :promoError="promoError"
      :isPromocodeLoading="isPromocodeLoading"
      v-model:selected-pack-id="packId"
      v-model:extra="extra"
      v-model:promo-code="promoCode"
      @showBuyCreditOrRedeemVoucherForm="changeProcessEvent"
      @applyPromocode="applyPromocode"
    />
  </div>
</template>

<script lang="ts">
import { ref, computed, defineComponent, onMounted, type Ref, type ComputedRef } from 'vue';
import phoneNumberParser from 'libphonenumber-js';
import sortBy from 'lodash/sortBy';
import partition from 'lodash/partition';
import BuyCreditOrRedeemVoucherForm from '@/components/BuyCreditOrRedeemVoucherForm.vue';
import BuyCredit from '@/components/BuyCredit.vue';
import { useMixpanel } from '@/composables';
import { fetchProducts, submitOrder } from '@/api/products';
import { checkPromo } from '@/api/promotions';
import { default as countries } from '@/data/countries.json';
import { Country } from '@/models/country';
import { NamedCountry } from '@/models/namedcountry';
import { VoucherResponse } from '@/types/vouchers';
import { PurchasePack, PurchasePacks } from '@/types/products';
import { TrackEvents } from '@/types/trackEvents';
import { fetchUserLocation } from './api/location';

declare global {
  interface Window {
    clientCountryCode: string;
  }
}

const DEFAULT_COUNTRY: Country = countries.find(({ code }) => code === window.clientCountryCode?.toLowerCase()) ?? {
  code: 'nl',
  prefix: '31',
  alt: [],
};

const parseSearchPhoneNumber = (searchParam: string) => {
  const result = phoneNumberParser(searchParam.replace(/^ /, '+'), 'NL');
  if (!result) {
    return {};
  }
  const country = countries.find(({ code }) => result.country?.toLowerCase() === code);
  return { country, phonenumber: result.nationalNumber.toString() };
};

enum ProcessEvent {
  BUY_CREDIT = 'buy_credit',
  PURCHASE_PACKS = 'purchase_packs',
}

export default defineComponent({
  name: 'ShopComponent',
  components: {
    BuyCredit,
    BuyCreditOrRedeemVoucherForm,
  },
  setup() {
    const { track } = useMixpanel();

    const searchParams = new URLSearchParams(location.search);
    const phoneSearch = searchParams.get('phone_number');
    const { phonenumber: searchPhonenumber = null, country: searchCountry = null } = phoneSearch
      ? parseSearchPhoneNumber(phoneSearch)
      : {};
    const country = ref(searchCountry ?? DEFAULT_COUNTRY);
    const phonenumber = ref(searchPhonenumber ?? ''); // 625524255 is an easy develop number
    const purchasePacks: Ref<PurchasePacks | null> = ref(null);
    const isLoading = ref(false);
    const _packId: Ref<number | null> = ref(null);
    const extra: Ref<{ amount: number | null; percentage: number | null } | null> = ref(null);
    const promoCode: Ref<string | null> = ref(null);
    const isPromocodeLoading = ref(false);
    const voucherResponse = ref<VoucherResponse | null>(null);
    const invalidPhonenumber = ref(false);
    const submitError = ref(false);
    const autoRecharge = ref(searchParams.get('auto_recharge') ? searchParams.get('auto_recharge') === 'true' : null);
    const isPhonePrefilled = ref(Boolean(phoneSearch));
    const webShopProcessEvent = ref<ProcessEvent>(ProcessEvent.BUY_CREDIT);
    const processLoading = ref<boolean>(false);
    const userLocationLoading = ref<boolean>(true);

    const packs: ComputedRef<null | PurchasePacks> = computed(() => {
      if (purchasePacks.value === null) {
        return null;
      }
      const { currency, products } = purchasePacks.value;
      const sorted = sortBy(products, 'amount');
      const [highlights, rest] = partition(sorted, 'highlight');
      return {
        currency,
        products: [...highlights, ...rest],
      };
    });

    const packId: ComputedRef<number | null> = computed({
      get: () => _packId.value ?? packs.value?.products[0]?.id ?? null,
      set: (val: number | null) => {
        _packId.value = val;
      },
    });

    const fullPhoneNumber: ComputedRef<string> = computed(() => `${country.value.prefix}${phonenumber.value}`);

    const isPurchasePackView = computed<boolean>(() => webShopProcessEvent.value === ProcessEvent.PURCHASE_PACKS);

    const selectedPack: ComputedRef<PurchasePack | null> = computed(
      () => purchasePacks.value?.products.find((product) => product.id === packId.value) ?? null,
    );

    const promoError = computed(() => {
      return extra.value !== null && extra.value.amount === null && extra.value.percentage === null;
    });

    onMounted(() => {
      track(TrackEvents.WEBSITE);
    });

    const applyPromocode = async (code: string) => {
      if (packs.value === null) {
        throw new Error("Can't submit without purchasePacks");
      }
      isPromocodeLoading.value = true;
      try {
        const result = await checkPromo(packs.value.currency, country.value, phonenumber.value, code);
        extra.value = result;

        if (result.amount === null && result.percentage === null) {
          track(TrackEvents.PROMO_CODE_FAILURE);
        } else {
          track(TrackEvents.PROMO_CODE_SUCCESS);
        }
        if (!promoError.value) {
          promoCode.value = code;
        }
      } catch {
        track(TrackEvents.PROMO_CODE_FAILURE);
      } finally {
        isPromocodeLoading.value = false;
      }
    };

    const updatePurchasePacks = async () => {
      try {
        isLoading.value = true;
        purchasePacks.value = await fetchProducts(country.value, phonenumber.value);
        const searchPromo = searchParams.get('promo_code');
        if (searchPromo != null && searchPromo != '') {
          applyPromocode(searchPromo);
          promoCode.value = searchPromo;
        }
        invalidPhonenumber.value = false;
        webShopProcessEvent.value = ProcessEvent.PURCHASE_PACKS;
        isLoading.value = false;
      } catch (e) {
        isLoading.value = false;
        invalidPhonenumber.value = true;
      }
    };

    const submit = async () => {
      if (packId.value === null) {
        throw new Error("Can't submit without packId");
      }
      if (purchasePacks.value === null) {
        throw new Error("Can't submit without purchasePacks");
      }
      try {
        isLoading.value = true;
        const url = await submitOrder(
          purchasePacks.value.currency,
          country.value,
          phonenumber.value,
          promoCode.value,
          packId.value,
          autoRecharge.value,
          document.documentElement.getAttribute('lang') ?? 'en',
        );
        window.location.href = url;
      } catch (e) {
        submitError.value = true;
        isLoading.value = false;
      }
    };

    const selectCountry = (updatedCountry: Country) => {
      country.value = updatedCountry;
      purchasePacks.value = null;
    };

    const changePhoneNumber = (phonenumberInput: string) => {
      phonenumber.value = phonenumberInput;
      purchasePacks.value = null;
      isPhonePrefilled.value = false;
    };

    const changeProcessEvent = () => {
      webShopProcessEvent.value =
        webShopProcessEvent.value === ProcessEvent.BUY_CREDIT ? ProcessEvent.PURCHASE_PACKS : ProcessEvent.BUY_CREDIT;
    };

    const changeAutoRecharge = (value: boolean) => {
      autoRecharge.value = value;
    };

    const next = () => {
      if (purchasePacks.value === null) {
        updatePurchasePacks();
      } else {
        submit();
      }
    };

    if (phoneSearch) {
      next();
    }

    onMounted(async () => {
      if (phonenumber.value) return (userLocationLoading.value = false);
      try {
        userLocationLoading.value = true;
        const userCountry = await fetchUserLocation();
        const foundCountry = countries.find(
          ({ code }) => userCountry.country_code.toLowerCase() === code.toLowerCase(),
        );
        if (foundCountry) country.value = foundCountry;
        userLocationLoading.value = false;
      } catch (error) {
        userLocationLoading.value = false;
        console.log('Error getting user location');
      }
    });

    return {
      invalidPhonenumber,
      packs,
      packId,
      autoRecharge,
      changeAutoRecharge,
      changePhoneNumber,
      phonenumber,
      country: country as unknown as NamedCountry,
      selectCountry,
      applyPromocode,
      extra,
      promoError,
      promoCode,
      isPromocodeLoading,
      submitError,
      selectedPack,
      phoneSearch,
      isPhonePrefilled,
      fullPhoneNumber,
      voucherResponse,
      processLoading,
      isPurchasePackView,
      isLoading,
      userLocationLoading,
      changeProcessEvent,
      submit,
      updatePurchasePacks,
    };
  },
});
</script>

<style lang="scss">
@import './fonts';

.buy-credit-wrapper {
  all: initial;
  background: white;
  min-height: 600px;

  * {
    color: initial;
    vertical-align: initial;
    transition: none;
    margin: initial;
    padding: initial;
    font-family: 'Noto Sans', sans-serif;
    -webkit-tap-highlight-color: transparent;
  }
  input:focus,
  input:hover {
    border: none;
    outline: none;
  }

  box-sizing: border-box;
  position: relative;

  display: flex;
  flex-direction: column;

  color: $color-text-primary;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  label {
    display: inline-block;
    margin-bottom: 9px;
    font-size: 14px;
    font-weight: bold;
    color: $color-label-text;
  }

  .input-container {
    @extend %primary-border;
    display: flex;
    padding: 1px;

    .prepend-element {
      display: flex;
      align-items: center;
      background: transparent;
      border: 0;
    }
  }

  .submit-phonenumber,
  .submit-order {
    @extend %primary-button;
    margin-top: 24px;
    height: 48px;
    width: 100%;
    font-size: 18px;
  }

  .submit-phonenumber {
    span {
      color: $white;
    }
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .secondary-btn {
    @extend %tertiary-button;
    font-size: 14px;
    margin-top: 16px;
    width: 100%;
  }

  .blank-styling {
    border: 0;
    background: transparent;
  }

  .prepend-element + input {
    padding-left: 6px;
  }

  input {
    height: 44px;
    font-size: $font-size-label;
    padding: 0 16px;
    margin-bottom: 0;

    &:not(.blank-styling) {
      @extend %primary-border;
    }
  }

  .container-step2 {
    display: flex;
    flex-direction: column;
    margin-top: 24px;
    margin-bottom: 16px;
  }

  .promo-error,
  .submit-error {
    color: $color-danger;
    font-size: 14px;
  }

  .header {
    margin-bottom: 16px;
  }

  .auto-recharge {
    margin-top: 16px;
    .auto-recharge__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
    }
    p {
      color: $color-text-primary;
      font-size: 12px;
    }
  }
}
</style>
